import store from '@/store';
import {
  finishUnauthenticatedBootstrap,
  getUserByToken,
} from '@/store/userSlice';
import { getAccessToken, storeAccessToken } from '@/utils/localStorage';
import { isEmbedPathname } from '@/utils/urlBase';

import type { AppDispatch } from '@/store';

/** Token currently being restored (`null` = no-token bootstrap done). */
let restoringToken: string | null | undefined;

/**
 * Persist embed `?token=` from the current URL into localStorage before any
 * session restore. SmartRecruiters (and other ATS hosts) mint a fresh JWT on
 * every iframe load; applying it synchronously prevents App boot from
 * restoring a leftover token and then restoring again after EmbedPluginRoute.
 */
export function applyEmbedUrlTokenIfPresent(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const { pathname, search } = window.location;
  if (!isEmbedPathname(pathname)) {
    return;
  }

  const urlToken = new URLSearchParams(search).get('token');
  if (!urlToken || urlToken === getAccessToken()) {
    return;
  }

  storeAccessToken(urlToken, 'Bearer');
}

/**
 * Starts session restore once per token when a token exists.
 * Safe to call from App boot and route guards — subsequent calls for the
 * same token are no-ops. A new token (e.g. plugin URL auth) triggers restore.
 */
export function restoreSessionIfNeeded(
  dispatch: AppDispatch = store.dispatch,
): void {
  const token = getAccessToken();
  const { isAuth } = store.getState().user;

  if (!token) {
    if (restoringToken === undefined) {
      restoringToken = null;
      dispatch(finishUnauthenticatedBootstrap());
    }
    return;
  }

  if (restoringToken === token) {
    return;
  }

  // Already authenticated from login (or a completed restore) and still the
  // same stored token — record it so a later embed URL token can be detected.
  if (isAuth && restoringToken == null) {
    restoringToken = token;
    return;
  }

  restoringToken = token;
  void dispatch(getUserByToken(token));
}

/** Test-only: reset the module guard between Vitest cases. */
export function resetSessionRestoreForTests(): void {
  restoringToken = undefined;
}
